<script setup lang="ts">
import {ExternalLink, Gauge, ScrollText, Wrench} from "@lucide/vue";
import {PropositionJustification} from "@/ai-assistant/interfaces";
import {RELIABILITY_LABELS} from "@/ai-assistant/constants";
import {measure} from "@/ai-assistant/lib/propositionFormat";

const {justification} = defineProps<{
    justification: PropositionJustification;
}>();


const reliability = (value?: string | null): string =>
    value ? RELIABILITY_LABELS[value] ?? value : '';
</script>

<template>
  <!--
    Four panels in the order the module reasons in: what was read, what was
    assumed, what constrains, and what remains unsolved. The last one is shown
    by the card above rather than here, so it is impossible to miss.
  -->
  <div class="space-y-2.5 border-t border-primary-lightest pt-2.5">
    <section v-if="justification.facts.length">
      <p class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
        <Gauge class="size-3 shrink-0" />
        Faits mesurés
      </p>
      <ul class="space-y-1">
        <li
          v-for="(fact, index) in justification.facts"
          :key="`fait-${index}`"
          class="flex items-baseline justify-between gap-2 text-[11px] leading-snug"
        >
          <span class="min-w-0 flex-1 text-gray-dark/80">{{ fact.label }}</span>
          <span class="shrink-0 font-semibold text-gray-dark">{{ measure(fact.value, fact.unit) }}</span>
          <!-- The tool it came from: what tells a measured figure from an asserted one. -->
          <code class="shrink-0 rounded bg-gray-light px-1 text-[9.5px] text-gray-dark/60">{{ fact.source }}</code>
        </li>
      </ul>
    </section>

    <section v-if="justification.assumptions.length">
      <p class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
        <Wrench class="size-3 shrink-0" />
        Hypothèses
      </p>
      <ul class="space-y-1">
        <li
          v-for="(hypothesis, index) in justification.assumptions"
          :key="`hypothese-${index}`"
          class="text-[11px] leading-snug text-gray-dark/80"
        >
          {{ hypothesis.label }} : <span class="font-semibold text-gray-dark">{{ measure(hypothesis.value) }}</span>
          <span v-if="reliability(hypothesis.reliability)" class="text-gray-dark/60">
            — {{ reliability(hypothesis.reliability) }}
          </span>
        </li>
      </ul>
    </section>

    <section v-if="justification.constraints.length">
      <p class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
        <ScrollText class="size-3 shrink-0" />
        Règles prises en compte
      </p>
      <ul class="space-y-1">
        <li
          v-for="constraint in justification.constraints"
          :key="constraint.rule_id"
          class="flex items-baseline gap-1.5 text-[11px] leading-snug text-gray-dark/80"
        >
          <ExternalLink class="mt-px size-2.5 shrink-0 text-gray-dark/40" />
          <span class="min-w-0 flex-1">{{ constraint.label }}</span>
          <code class="shrink-0 rounded bg-gray-light px-1 text-[9.5px] text-gray-dark/60">{{ constraint.rule_id }}</code>
        </li>
      </ul>
    </section>
  </div>
</template>
